import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-input-group';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-typeahead';
import { IMcTypeaheadData } from '@maersk-global/mds-components-core/mc-typeahead/types';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-label';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';

import { ToastrService } from '../../services/toastr-service';
import { BookingAssignmentService } from '../../services/booking-assignment.service';
import { BookingAssignmentStateService } from '../../services/booking-assignment-state.service';
import { AuthHelper } from '../../auth/auth-helper';
import { BookingAssignment } from '../../models/booking-assignment.model';

@Component({
  selector: 'app-booking-assignment',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxSkeletonLoaderModule],
  templateUrl: './booking-assignment.component.html',
  styleUrls: ['./booking-assignment.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BookingAssignmentComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly bookingAssignmentService = inject(BookingAssignmentService);
  private readonly toaster = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly state = inject(BookingAssignmentStateService);

  totalPagesCount = 1;
  totalFilteredCount = 0;
  loading = false;
  filterTags: { key: string; value: string }[] = [];
  currentSortDirection = '';
  currentSortColumn = '';

  // Specific-search mode: activated when user clicks the search button with a booking number
  searchMode = false;
  searchLoading = false;
  searchedBookingNumber = '';
  searchResults: Array<BookingAssignment & { assignment: string }> = [];

  private cachedSearchEntry: (BookingAssignment & { assignment: string }) | null = null;

  constructor() {
    // Persist filter changes so they survive navigation back from the detail page.
    this.filterForm.valueChanges.subscribe(() => {
      const raw = this.filterForm.getRawValue();
      this.state.saveListPageSnapshot(raw.maintenanceShopCode ?? '');
    });
  }

  maintenanceShops = AuthHelper.user?.maintenanceShops ?? [];

  readonly shopTypeaheadData: IMcTypeaheadData[] = this.maintenanceShops.map((s) => ({
    label: `${s.maintenanceShopCode} - ${s.maintenanceShopName}`,
    value: s.maintenanceShopCode,
  }));

  private readonly _restoredShopCode =
    this.state.listPageSnapshot()?.maintenanceShopCode ?? this.maintenanceShops[0]?.maintenanceShopCode ?? '';

  readonly initialShopData: IMcTypeaheadData | undefined = (() => {
    const code = this._restoredShopCode;
    const shop = this.maintenanceShops.find((s) => s.maintenanceShopCode === code) ?? this.maintenanceShops[0];
    return shop ? { label: `${shop.maintenanceShopCode} - ${shop.maintenanceShopName}`, value: shop.maintenanceShopCode } : undefined;
  })();

  filterForm = this.formBuilder.group({
    maintenanceShopCode: this.formBuilder.control<string>({
      value: this._restoredShopCode,
      disabled: this.maintenanceShops.length === 1,
    }),
    bookingNumber: this.formBuilder.control<string | null>(null),
  });

  columns: TableColumn[] = [
    { id: 'bookingNumber', label: 'Booking Number', sortDisabled: true },
    { id: 'assignment', label: 'Assignment', sortDisabled: true },
  ];

  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 20 }, (_, i) => ({ id: i }));

  paginationSubject$$ = new BehaviorSubject<{ currentPage: number; pageLimit: number }>({ currentPage: 1, pageLimit: 20 });
  pagination$ = this.paginationSubject$$.asObservable();

  // Combined stream: re-fetches from the server whenever shop, page, or page size changes.
  tableData$ = combineLatest([
    this.filterForm.get('maintenanceShopCode')!.valueChanges.pipe(
      startWith(this.filterForm.getRawValue().maintenanceShopCode ?? ''),
      distinctUntilChanged(),
      tap(() => this.paginationSubject$$.next({ ...this.paginationSubject$$.value, currentPage: 1 })),
    ),
    this.pagination$,
  ]).pipe(
    debounceTime(50),
    tap(() => {
      this.loading = true;
      this.setFilterTags();
    }),
    switchMap(([shopCode, pagination]) =>
      this.bookingAssignmentService.fetchBookingAssignmentsPage(shopCode ?? '', pagination.currentPage, pagination.pageLimit).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 404) {
            this.toaster.showToastr('Failed to load booking assignments', 'error');
          }
          this.loading = false;
          return of(null);
        }),
      ),
    ),
    tap((res) => {
      if (res) {
        const pageLimit = this.paginationSubject$$.value.pageLimit;
        this.totalPagesCount = res.totalPages || 1;
        this.totalFilteredCount = res.totalCount > 0 ? res.totalCount : res.totalPages * pageLimit;
      }
      this.loading = false;
    }),
    map((res) =>
      (res?.items ?? []).map((row) => ({
        ...row,
        assignment: `${row.totalAssignedEquipmentQuantity} out of ${row.totalBookedEquipmentQuantity}`,
      })),
    ),
  );

  onShopSelected(event: Event): void {
    const detail = (event as CustomEvent).detail as IMcTypeaheadData;
    if (detail?.value != null) {
      this.filterForm.get('maintenanceShopCode')?.setValue(String(detail.value));
    }
  }
  // Called when user clicks the search button or presses Enter in the booking number field.
  // Uses the specific booking detail endpoint instead of client-side filtering.
  onSearch(): void {
    const form = this.filterForm.getRawValue();
    const shopCode = form.maintenanceShopCode ?? '';
    const bookingNumber = (form.bookingNumber ?? '').trim();
    if (!bookingNumber) return;

    this.searchMode = true;
    this.searchLoading = true;
    this.searchedBookingNumber = bookingNumber;
    this.searchResults = [];

    this.bookingAssignmentService.getBookingDetail(shopCode, bookingNumber).subscribe({
      next: (response) => {
        this.cachedSearchEntry = {
          bookingNumber: response.bookingNumber,
          maintenanceShopCode: shopCode,
          totalBookedEquipmentQuantity: response.totalBookedEquipmentQuantity,
          totalAssignedEquipmentQuantity: response.totalAssignedEquipmentQuantity,
          nonCompliantEquipmentQuantity: response.nonCompliantEquipmentQuantity,
          assignment: `${response.totalAssignedEquipmentQuantity} out of ${response.totalBookedEquipmentQuantity}`,
        };
        this.searchResults = this.cachedSearchEntry ? [this.cachedSearchEntry] : [];
        this.searchLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.searchLoading = false;
        this.searchResults = [];
        if (error.status !== 404) {
          this.toaster.showToastr('Failed to search booking', 'error');
        }
      },
    });
  }

  onClearSearch(): void {
    this.filterForm.get('bookingNumber')?.setValue(null);
    this.searchMode = false;
    this.searchLoading = false;
    this.searchedBookingNumber = '';
    this.searchResults = [];
    this.cachedSearchEntry = null;
  }

  setFilterTags(): void {
    this.filterTags = [];
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.paginationSubject$$.next({ currentPage: 1, pageLimit: customEvent.detail });
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.paginationSubject$$.value.currentPage) {
      this.paginationSubject$$.next({ currentPage: changedPage, pageLimit: this.paginationSubject$$.value.pageLimit });
    }
  }

  onSortChange(_event: Event): void {
    // Sorting is handled server-side and not yet supported by the API. No-op for now.
  }

  clearFilter(controlName: string): void {
    this.filterForm.get(controlName)?.setValue(null);
  }

  clearAllFilters(): void {
    this.filterForm.patchValue({ bookingNumber: null });
    this.searchMode = false;
    this.searchedBookingNumber = '';
    this.searchResults = [];
    this.cachedSearchEntry = null;
  }

  onRowClick(event: Event): void {
    const detail = (event as CustomEvent).detail as { rowData?: BookingAssignment } | undefined;
    const bookingNumber = detail?.rowData?.bookingNumber;
    const shopCode = detail?.rowData?.maintenanceShopCode ?? '';
    if (bookingNumber) {
      this.router.navigate(['/booking-assignment', shopCode, bookingNumber]);
    }
  }
}
